import { Component } from '@theme/component';
import { closest, clamp, center, getVisibleElements } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';
import { Scroller } from '@theme/scrolling';
import { cycleFocus } from '@theme/focus';

/**
 * Collection links component
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} container
 * @property {HTMLElement[]} [images]
 * @property {HTMLElement[]} [links]
 * @property {import('slideshow').Slideshow} slideshow
 *
 * @extends {Component<Refs>}
 */
class CollectionLinks extends Component {
  requiredRefs = ['container'];

  /** @type {Scroller} */
  #scroll;

  #browseAbort;
  #touchQuery;
  #browseAnimation;

  get isBrowse() { return this.hasAttribute('data-mervaux-browse'); }
  get isTouchBrowse() {
    return this.isBrowse && Boolean(this.#touchQuery?.matches);
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);

    this.#scroll = new Scroller(this.refs.container, { onScroll: this.#handleScroll });
    if (this.isBrowse) this.#setupBrowse();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#scroll.destroy();
    this.#browseAbort?.abort();
    this.#browseAnimation?.cancel();
    this.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
  }

  get links() {
    return this.refs.links || [];
  }

  get currentIndex() {
    return this.links.findIndex((link) => link.getAttribute('aria-current') === 'true');
  }

  /**
   * Public method to select a collection link
   *
   * @param {number} targetIndex
   * @param {PointerEvent} [event]
   */
  select(targetIndex, event) {
    if (this.isTouchBrowse && event?.type === 'pointerenter') return;
    const previousIndex = this.currentIndex;
    this.#updateSelectedLink(targetIndex);
    this.refs.slideshow?.select(targetIndex, undefined, { animate: false });
    if (this.isBrowse && previousIndex !== this.currentIndex && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const image = this.refs.slideshow?.querySelectorAll('slideshow-slide')[this.currentIndex]?.querySelector('img');
      this.#browseAnimation?.cancel();
      this.#browseAnimation = image?.animate([{ opacity: .55 }, { opacity: 1 }], {
        duration: 360,
        easing: 'cubic-bezier(.2, .65, .3, 1)',
      });
    }
    if (event) this.#revealImage(event);
  }

  #setupBrowse() {
    this.#browseAbort?.abort();
    this.#browseAbort = new AbortController();
    const { signal } = this.#browseAbort;
    this.#touchQuery = matchMedia('(max-width: 749px), (hover: none), (pointer: coarse)');
    const syncMode = () => this.toggleAttribute('data-browse-touch', this.isTouchBrowse);
    this.#touchQuery.addEventListener('change', syncMode, { signal });
    syncMode();
    let imageGesture;
    const trackImageGesture = (event) => {
      if (!imageGesture || imageGesture.pointerId !== event.pointerId) return;
      if (Math.hypot(event.clientX - imageGesture.x, event.clientY - imageGesture.y) > 12) {
        imageGesture.moved = true;
      }
    };
    this.addEventListener('pointerdown', (event) => {
      imageGesture = undefined;
      if (!this.isTouchBrowse || !(event.target instanceof Element)) return;
      if (!event.target.closest('a.collection-links__image')) return;
      imageGesture = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
    }, { signal, capture: true, passive: true });
    this.addEventListener('pointermove', trackImageGesture, { signal, capture: true, passive: true });
    this.addEventListener('pointerup', trackImageGesture, { signal, capture: true, passive: true });
    this.addEventListener('pointercancel', (event) => {
      if (imageGesture?.pointerId === event.pointerId) imageGesture.moved = true;
    }, { signal, capture: true, passive: true });
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const choice = event.target.closest('[data-browse-select]');
      if (choice) {
        event.preventDefault();
        event.stopPropagation();
        this.select(Number(choice.dataset.browseSelect), event);
        return;
      }
      if (!this.isTouchBrowse) return;
      const link = event.target.closest('.collection-links__link');
      if (link) {
        event.preventDefault();
        this.select(this.links.indexOf(link), event);
      }
      if (event.target.closest('a.collection-links__image')) {
        if (imageGesture?.moved && event.detail !== 0) {
          event.preventDefault();
          event.stopPropagation();
        }
        imageGesture = undefined;
      }
    }, { signal, capture: true });
    this.#syncBrowseControls();
  }

  #syncBrowseControls() {
    if (!this.isBrowse) return;
    const link = this.links[Math.max(0, this.currentIndex)];
    const explore = this.querySelector('[data-browse-explore]');
    if (link && explore) {
      explore.setAttribute('href', link.getAttribute('href'));
      const title = link.cloneNode(true);
      title.querySelectorAll('sup').forEach((node) => node.remove());
      explore.textContent = explore.dataset.browseLabel.replace('__collection__', title.textContent.trim());
    }
    this.querySelectorAll('[data-browse-select]').forEach((button) => {
      button.setAttribute('aria-pressed', String(Number(button.dataset.browseSelect) === Math.max(0, this.currentIndex)));
    });
    this.querySelectorAll('a.collection-links__image').forEach((imageLink, index) => {
      imageLink.setAttribute('tabindex', index === Math.max(0, this.currentIndex) ? '0' : '-1');
    });
  }

  /**
   * Update the selected link
   *
   * @param {number} index
   */
  #updateSelectedLink(index) {
    const { links } = this;
    const selectedIndex = clamp(index, 0, links.length - 1);

    for (const [index, link] of links.entries()) {
      link.setAttribute('aria-current', Boolean(index === selectedIndex).toString());
    }
    this.#syncBrowseControls();
  }

  /**
   * Handle slideshow select event
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = async (event) => {
    if (!event.detail.userInitiated) return;

    const { index } = event.detail;
    if (index === this.currentIndex) return;

    const selectedLink = this.links[index];
    if (!selectedLink) return;

    this.#updateSelectedLink(index);

    if (this.isBrowse) return;

    this.#scroll.to(selectedLink);
  };

  /**
   * Cycle focus to the next or previous link
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    const choice = event.target instanceof Element && event.target.closest('[data-browse-select]');
    if (choice) {
      const choices = [...this.querySelectorAll('[data-browse-select]')];
      const index = choices.indexOf(choice);
      let nextIndex;
      switch (event.key) {
        case 'ArrowRight': nextIndex = index + 1; break;
        case 'ArrowLeft': nextIndex = index - 1; break;
        case 'ArrowDown': nextIndex = index + 2; break;
        case 'ArrowUp': nextIndex = index - 2; break;
        case 'Home': nextIndex = 0; break;
        case 'End': nextIndex = choices.length - 1; break;
        default: return;
      }
      event.preventDefault();
      nextIndex = (nextIndex + choices.length) % choices.length;
      choices[nextIndex].focus();
      this.select(nextIndex, event);
      return;
    }
    if (!(event.target instanceof Element) || !event.target.closest('.collection-links__link')) return;
    let modifier = 0;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        modifier = 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        modifier = -1;
        break;
    }

    if (!modifier) return;

    event.preventDefault();
    cycleFocus(this.links, modifier);
  }

  /**
   * Handle scroll event
   */
  #handleScroll = () => {
    if (this.isBrowse) return;
    const { links } = this;
    const { container } = this.refs;
    const visibleLinks = getVisibleElements(this, links, 0.1);

    if (visibleLinks.length === 0) return;
    const centers = visibleLinks.map((link) => center(link, 'x'));
    const referencePoint = center(container, 'x');
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleLink = visibleLinks[centers.indexOf(closestCenter)];

    if (!closestVisibleLink) return;

    const index = links.indexOf(closestVisibleLink);

    this.select(index);
  };

  /**
   * Clear all selections
   */
  clearSelections = () => {
    // Clear all selections when mouse leaves container
    const { links } = this;
    const { images } = this.refs;

    // Reset all links to unselected state (opacity will reset via CSS)
    for (const link of links) {
      link.setAttribute('aria-current', 'false');
    }

    // Hide any revealed images
    if (images) {
      for (const image of images) {
        image.removeAttribute('reveal');
      }
    }
  };

  /**
   * Reveal an image
   *
   * @param {Event} event
   */
  #revealImage(event) {
    if (!(event instanceof PointerEvent)) return;
    if (event.pointerType === 'touch') return;

    const { target } = event;
    if (!(target instanceof HTMLElement)) return;

    const { images } = this.refs;
    const index = this.links.indexOf(target);
    const selectedImage = images?.[index];

    if (!selectedImage) return;

    // Cache image dimensions to avoid repeated layout reads
    let cachedImageHeight = selectedImage.offsetHeight;
    let cachedImageWidth = selectedImage.offsetWidth;

    /** @type {number | null} */
    let rafId = null;

    /** @param {PointerEvent} event */
    const updateImagePosition = (event) => {
      // Throttle with requestAnimationFrame to avoid layout thrashing
      if (rafId !== null) return;

      rafId = requestAnimationFrame(() => {
        rafId = null;

        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const offset = 15;

        const wouldBeCutOff = event.clientY + cachedImageHeight + offset > viewportHeight;
        const yPos = wouldBeCutOff ? event.clientY - cachedImageHeight - offset : event.clientY + offset;

        const xPos = Math.min(Math.max(offset, event.clientX + offset), viewportWidth - cachedImageWidth - offset);

        selectedImage.style.setProperty('--x', `${xPos}px`);
        selectedImage.style.setProperty('--y', `${yPos}px`);
      });
    };

    const reset = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      selectedImage.removeAttribute('reveal');
      target.removeEventListener('mousemove', updateImagePosition);
    };

    updateImagePosition(event);

    for (const image of images) {
      if (image === selectedImage) {
        image.setAttribute('reveal', '');
      } else {
        image.removeAttribute('reveal');
      }
    }

    target.addEventListener('mousemove', updateImagePosition);
    target.addEventListener('mouseleave', reset, { once: true });
  }
}

if (!customElements.get('collection-links-component')) {
  customElements.define('collection-links-component', CollectionLinks);
}
